import React, { useState, useEffect, useCallback, useContext } from "react";
import { ThemeContext } from "../../../context/ThemeContext";

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const BuildingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" />
    <path d="M16 6h.01" />
    <path d="M12 6h.01" />
    <path d="M12 10h.01" />
    <path d="M12 14h.01" />
    <path d="M16 10h.01" />
    <path d="M16 14h.01" />
    <path d="M8 10h.01" />
    <path d="M8 14h.01" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const FileTextIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SaveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 4v6h-6" />
    <path d="M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const GeneralSettings = () => {
  const { theme } = useContext(ThemeContext); // ← Theme Context

  // ─── STATE ─────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    companyName: "Zaptodoor",
    contact: "+91 9200018690",
    headerText: "Zaptodoor",
    callNumber: "+91 9200018690",
    email: "info@zaptodoor.com",
    domain: "https://zaptodoor.com",
    salesEmail: "info@zaptodoor.com",
    address: "Gwalior Smart city incubation center, Moti Mahal, Gwalior, Madhya Pradesh",
    facebook: "https://www.facebook.com/profile.php?id=61570458",
    twitter: "https://x.com/zap_to_door",
    instagram: "https://www.instagram.com/zaptodoor",
    linkedin: "https://linkedin.com",
    youtube: "https://www.youtube.com/@zaptodoor",
    whatsapp: "https://wa.me/message/WWB7OGYJ5NEUK1",
    footerText: "",
  });
  const [originalData, setOriginalData] = useState({});

  // ─── API FUNCTIONS ─────────────────────────────────────────────
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      setTimeout(() => {
        const mockData = { ...formData };
        setFormData(mockData);
        setOriginalData(mockData);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error("Fetch settings error:", error);
      showToast("Failed to load settings", "error");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      showToast("Settings saved successfully", "success");
      setOriginalData({ ...formData });
    } catch (error) {
      console.error("Save settings error:", error);
      showToast("Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = () => {
    setFormData({ ...originalData });
    showToast("Reset to saved settings", "info");
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ─── STYLES ────────────────────────────────────────────────────
  const HEADER_OFFSET = 0;

  const inputStyle = {
    padding: "10px 14px",
    background: theme.surfaceLighter,
    border: `1px solid ${theme.border}`,
    borderRadius: "10px",
    fontSize: "13px",
    color: theme.textPrimary,
    outline: "none",
    transition: "all 0.2s",
    boxSizing: "border-box",
    fontFamily: "inherit",
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: 700,
    color: theme.textSecondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "6px",
    display: "block",
  };

  // ─── Loading State ─────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        height: `calc(100vh - ${HEADER_OFFSET}px)`,
        background: theme.background,
        fontFamily: "'DM Sans', 'Inter', sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: "56px",
            height: "56px",
            border: `3px solid ${theme.border}`,
            borderTopColor: theme.primary,
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 20px",
          }} />
          <p style={{ color: theme.textMuted, fontSize: "14px", margin: 0 }}>
            Loading settings...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: `calc(100vh - ${HEADER_OFFSET}px)`,
      background: theme.background,
      fontFamily: "'DM Sans', 'Inter', sans-serif",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
      transition: "background 0.3s ease",
    }}>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${theme.border}; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: ${theme.primary}; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: ${theme.primary}cc; }
        * { scrollbar-width: thin; scrollbar-color: ${theme.primary} ${theme.border}; }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      {/* ─── SCROLLABLE CONTENT ─────────────────────────────────── */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "24px 32px",
        scrollBehavior: "smooth",
      }}>
        {/* Header */}
        <div style={{ marginBottom: "28px", display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            width: "52px",
            height: "52px",
            borderRadius: "14px",
            background: `linear-gradient(135deg, ${theme.primary}, ${theme.primary}cc)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            boxShadow: `0 4px 12px ${theme.primary}40`,
          }}>
            <SettingsIcon />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: "24px",
              fontWeight: 700,
              color: theme.textPrimary,
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}>
              General Settings
              {hasChanges() && (
                <span style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: theme.warning,
                  background: `${theme.warning}1a`,
                  padding: "3px 10px",
                  borderRadius: "20px",
                  border: `1px solid ${theme.warning}30`,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}>
                  <span style={{
                    width: "5px",
                    height: "5px",
                    borderRadius: "50%",
                    background: theme.warning,
                  }} />
                  Unsaved Changes
                </span>
              )}
            </h1>
            <p style={{ fontSize: "13px", color: theme.textMuted, margin: "4px 0 0" }}>
              Manage your company information and social media links
            </p>
          </div>
        </div>

        {/* ─── Basic Information Card ──────────────────────────────── */}
        <div style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: "16px",
          padding: "24px",
          marginBottom: "20px",
          boxShadow: `0 4px 12px ${theme.primary}08`,
        }}>
          <h2 style={{
            fontSize: "15px",
            fontWeight: 700,
            color: theme.textPrimary,
            margin: "0 0 20px 0",
            paddingBottom: "14px",
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              background: `${theme.primary}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.primary,
            }}>
              <BuildingIcon />
            </div>
            Basic Information
          </h2>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
            gap: "20px",
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Company Name</label>
              <input
                type="text"
                style={inputStyle}
                value={formData.companyName}
                onChange={(e) => handleInputChange("companyName", e.target.value)}
                placeholder="Enter company name"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Header Text</label>
              <input
                type="text"
                style={inputStyle}
                value={formData.headerText}
                onChange={(e) => handleInputChange("headerText", e.target.value)}
                placeholder="Enter header text"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Contact Number</label>
              <input
                type="text"
                style={inputStyle}
                value={formData.contact}
                onChange={(e) => handleInputChange("contact", e.target.value)}
                placeholder="Enter contact number"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Call Number</label>
              <input
                type="text"
                style={inputStyle}
                value={formData.callNumber}
                onChange={(e) => handleInputChange("callNumber", e.target.value)}
                placeholder="Enter call number"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Email Address</label>
              <input
                type="email"
                style={inputStyle}
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Enter email address"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Sales Email</label>
              <input
                type="email"
                style={inputStyle}
                value={formData.salesEmail}
                onChange={(e) => handleInputChange("salesEmail", e.target.value)}
                placeholder="Enter sales email"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Domain</label>
              <input
                type="text"
                style={inputStyle}
                value={formData.domain}
                onChange={(e) => handleInputChange("domain", e.target.value)}
                placeholder="Enter website domain"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px", gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Address</label>
              <textarea
                style={{ ...inputStyle, resize: "vertical", minHeight: "80px", fontFamily: "inherit" }}
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder="Enter company address"
                rows="3"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>
          </div>
        </div>

        {/* ─── Social Media Card ───────────────────────────────────── */}
        <div style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: "16px",
          padding: "24px",
          marginBottom: "20px",
          boxShadow: `0 4px 12px ${theme.primary}08`,
        }}>
          <h2 style={{
            fontSize: "15px",
            fontWeight: 700,
            color: theme.textPrimary,
            margin: "0 0 20px 0",
            paddingBottom: "14px",
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              background: `${theme.primary}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.primary,
            }}>
              <GlobeIcon />
            </div>
            Social Media Links
          </h2>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
            gap: "20px",
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Facebook</label>
              <input
                type="url"
                style={inputStyle}
                value={formData.facebook}
                onChange={(e) => handleInputChange("facebook", e.target.value)}
                placeholder="https://facebook.com/yourpage"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
              <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "2px" }}>
                Your Facebook page URL
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Twitter / X</label>
              <input
                type="url"
                style={inputStyle}
                value={formData.twitter}
                onChange={(e) => handleInputChange("twitter", e.target.value)}
                placeholder="https://twitter.com/yourhandle"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Instagram</label>
              <input
                type="url"
                style={inputStyle}
                value={formData.instagram}
                onChange={(e) => handleInputChange("instagram", e.target.value)}
                placeholder="https://instagram.com/yourhandle"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>LinkedIn</label>
              <input
                type="url"
                style={inputStyle}
                value={formData.linkedin}
                onChange={(e) => handleInputChange("linkedin", e.target.value)}
                placeholder="https://linkedin.com/company/yourcompany"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>YouTube</label>
              <input
                type="url"
                style={inputStyle}
                value={formData.youtube}
                onChange={(e) => handleInputChange("youtube", e.target.value)}
                placeholder="https://youtube.com/@yourchannel"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>WhatsApp</label>
              <input
                type="url"
                style={inputStyle}
                value={formData.whatsapp}
                onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                placeholder="https://wa.me/yournumber"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
              <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "2px" }}>
                WhatsApp chat link
              </div>
            </div>
          </div>
        </div>

        {/* ─── Footer Settings Card ────────────────────────────────── */}
        <div style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: "16px",
          padding: "24px",
          marginBottom: "20px",
          boxShadow: `0 4px 12px ${theme.primary}08`,
        }}>
          <h2 style={{
            fontSize: "15px",
            fontWeight: 700,
            color: theme.textPrimary,
            margin: "0 0 20px 0",
            paddingBottom: "14px",
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              background: `${theme.primary}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.primary,
            }}>
              <FileTextIcon />
            </div>
            Footer Settings
          </h2>

          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "20px",
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>Footer Text</label>
              <textarea
                style={{ ...inputStyle, resize: "vertical", minHeight: "80px", fontFamily: "inherit" }}
                value={formData.footerText}
                onChange={(e) => handleInputChange("footerText", e.target.value)}
                placeholder="Enter footer text (copyright, disclaimer, etc.)"
                rows="3"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
              <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "2px" }}>
                This text will appear in the website footer
              </div>
            </div>
          </div>
        </div>

        {/* Spacer for fixed footer */}
        <div style={{ height: "20px" }} />
      </div>

      {/* ─── FIXED FOOTER WITH BUTTONS ─────────────────────────────── */}
      <div style={{
        position: "sticky",
        bottom: 0,
        left: 0,
        right: 0,
        background: theme.surface,
        borderTop: `1px solid ${theme.border}`,
        padding: "16px 32px",
        zIndex: 100,
        boxShadow: `0 -4px 12px ${theme.primary}08`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {hasChanges() ? (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              borderRadius: "20px",
              background: `${theme.warning}15`,
              border: `1px solid ${theme.warning}30`,
              fontSize: "12px",
              fontWeight: 600,
              color: theme.warning,
            }}>
              <AlertIcon />
              You have unsaved changes
            </div>
          ) : (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              borderRadius: "20px",
              background: `${theme.success}15`,
              border: `1px solid ${theme.success}30`,
              fontSize: "12px",
              fontWeight: 600,
              color: theme.success,
            }}>
              <CheckIcon />
              All changes saved
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            style={{
              padding: "10px 22px",
              background: "transparent",
              color: theme.textPrimary,
              border: `1px solid ${theme.border}`,
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: !hasChanges() || saving ? "not-allowed" : "pointer",
              opacity: !hasChanges() || saving ? 0.5 : 1,
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontFamily: "inherit",
            }}
            onClick={resetSettings}
            disabled={!hasChanges() || saving}
            onMouseEnter={(e) => {
              if (hasChanges() && !saving) {
                e.currentTarget.style.background = theme.surfaceLighter;
                e.currentTarget.style.borderColor = theme.textMuted;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = theme.border;
            }}
          >
            <RefreshIcon /> Reset
          </button>
          <button
            style={{
              padding: "10px 24px",
              background: !hasChanges() || saving ? theme.textMuted : theme.primary,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: !hasChanges() || saving ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              boxShadow: !hasChanges() || saving ? "none" : `0 4px 12px ${theme.primary}40`,
              fontFamily: "inherit",
            }}
            onClick={saveSettings}
            disabled={!hasChanges() || saving}
            onMouseEnter={(e) => {
              if (hasChanges() && !saving) {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = `0 6px 16px ${theme.primary}60`;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = !hasChanges() || saving ? "none" : `0 4px 12px ${theme.primary}40`;
            }}
          >
            {saving ? (
              <>
                <div style={{
                  width: "12px",
                  height: "12px",
                  border: "2px solid rgba(255,255,255,0.3)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }} />
                Saving...
              </>
            ) : (
              <>
                <SaveIcon /> Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* ─── TOAST NOTIFICATION ────────────────────────────────────── */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: "90px",
          right: "24px",
          zIndex: 1100,
          padding: "12px 20px",
          borderRadius: "12px",
          fontSize: "13px",
          fontWeight: 500,
          animation: "slideUp 0.25s ease",
          background: theme.surface,
          border: `1px solid ${
            toast.type === "error" ? (theme.error || theme.danger) :
            toast.type === "info" ? theme.primary :
            theme.success
          }40`,
          color: toast.type === "error" ? (theme.error || theme.danger) :
                 toast.type === "info" ? theme.primary :
                 theme.success,
          boxShadow: `0 8px 32px ${theme.primary}20`,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <div style={{
            width: "28px",
            height: "28px",
            borderRadius: "8px",
            background: toast.type === "error" ? `${theme.error || theme.danger}15` :
                       toast.type === "info" ? `${theme.primary}15` :
                       `${theme.success}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            {toast.type === "error" ? <AlertIcon /> :
             toast.type === "info" ? <AlertIcon /> :
             <CheckIcon />}
          </div>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default GeneralSettings;